/**
 * SlingshotSkillEffect — Astral dense-path active: orbital debris burst.
 *
 * Fires the player's orbiting debris (from OrbitalDebrisSystem) at a single
 * target. Damage scales with orbit count: up to 5 objects at 12 damage each,
 * maximum 60-damage burst plus stagger. Empty orbit means no activation.
 *
 * Phase sequence: active (0ms, instantaneous fire) → cooldown (8000ms).
 *
 * Upgrade hooks (resolver-routed):
 * - Scatter Shot (slingshot_upgrade_2): when `coneAngleDegrees` is present,
 *   switches from single-target to cone fire — up to 2 secondary targets
 *   take 40% of burst damage.
 *
 * Design values (from design-decisions.md § Astral Base Values):
 * - Damage per debris: 12
 * - Stagger duration: 400ms
 * - Cooldown: 8000ms (long tier)
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see astral-spec.md § slingshot
 * @see design-decisions.md § Astral Base Values
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface SlingshotDeps {
  readonly resolver: {
    hasEffect(skillId: string, key: string): boolean;
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly orbitalDebris: {
    readonly count: number;
    consumeAll(): number;
  };
  readonly statusEffects: {
    apply(statusId: string, durationMs: number, sourceId: string): void;
    isImmune(statusId: string): boolean;
  };
  readonly combat: {
    dealDamage(enemyId: string, damage: number): void;
  };
  readonly enemies: {
    /** Returns the primary target enemy or null if no targets in range. */
    getPrimaryTarget(): string | null;
    /**
     * Find secondary targets in a cone from the player toward the primary
     * target. Returns up to maxTargets enemy IDs (excludes the primary).
     */
    getSecondariesInCone(
      primaryTargetId: string,
      angleDegrees: number,
      maxTargets: number,
    ): string[];
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface SlingshotConfig {
  /** Long-tier cooldown in ms (8000). */
  readonly cooldownMs: number;
  /** Damage dealt per consumed debris object (12). */
  readonly baseDamagePerDebris: number;
  /** Stagger duration applied to the primary target in ms (400). */
  readonly staggerDurationMs: number;
}

const DEFAULT_CONFIG: SlingshotConfig = {
  cooldownMs: 8000,
  baseDamagePerDebris: 12,
  staggerDurationMs: 400,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class SlingshotSkillEffect extends PhasedSkillEffect {
  override readonly skillId = 'slingshot';

  private readonly deps: SlingshotDeps;
  readonly config: SlingshotConfig;

  private _debrisConsumed = 0;
  private _totalDamageDealt = 0;
  private _staggerApplied = false;
  private _activationsCount = 0;
  private _lastFailLabel: string | null = null;
  private _secondaryTargetsHit = 0;

  constructor(deps: SlingshotDeps, config: Partial<SlingshotConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get debrisConsumed(): number { return this._debrisConsumed; }
  get totalDamageDealt(): number { return this._totalDamageDealt; }
  get staggerApplied(): boolean { return this._staggerApplied; }
  get activationsCount(): number { return this._activationsCount; }
  get lastFailLabel(): string | null { return this._lastFailLabel; }
  get secondaryTargetsHit(): number { return this._secondaryTargetsHit; }

  // --- PhasedSkillEffect implementation ---

  protected override readonly phaseSequence: readonly SkillPhase[] = ['active', 'cooldown'];

  protected override getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'active': return 0; // instantaneous — fire in onPhaseEnter
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected override canActivateExtra(): boolean {
    if (this.deps.orbitalDebris.count <= 0) {
      this._lastFailLabel = 'EMPTY ORBIT';
      return false;
    }
    this._lastFailLabel = null;
    return true;
  }

  protected override onActivate(): void {
    this._activationsCount++;
    this._staggerApplied = false;
    this._secondaryTargetsHit = 0;
  }

  protected override onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._fireBurst();
    }
  }

  // --- Internal helpers ---

  private _fireBurst(): void {
    // Consume all debris from the orbital system.
    const count = this.deps.orbitalDebris.consumeAll();
    this._debrisConsumed = count;

    const burstDamage = count * this.config.baseDamagePerDebris;

    // Find primary target.
    const primaryTarget = this.deps.enemies.getPrimaryTarget();
    if (primaryTarget === null) return; // no target in range

    // Deal burst damage to primary target.
    this.deps.combat.dealDamage(primaryTarget, burstDamage);
    this._totalDamageDealt += burstDamage;

    // Apply stagger to primary target (ADR-065: check immunity first).
    if (!this.deps.statusEffects.isImmune('stagger')) {
      this.deps.statusEffects.apply(
        'stagger',
        this.config.staggerDurationMs,
        'slingshot',
      );
      this._staggerApplied = true;
    }

    // Scatter Shot upgrade: cone mode when coneAngleDegrees is registered.
    if (this.deps.resolver.hasEffect('slingshot', 'coneAngleDegrees')) {
      const coneAngle = this.deps.resolver.getAdditiveEffect(
        'slingshot', 'coneAngleDegrees',
      );
      const maxSplash = this.deps.resolver.getAdditiveEffect(
        'slingshot', 'maxSplashTargets',
      );
      const splashMult = this.deps.resolver.getAdditiveEffect(
        'slingshot', 'splashDamageMultiplier',
      );

      const splashDamage = Math.round(burstDamage * splashMult);
      const secondaries = this.deps.enemies.getSecondariesInCone(
        primaryTarget,
        coneAngle,
        maxSplash,
      );

      for (const enemyId of secondaries) {
        this.deps.combat.dealDamage(enemyId, splashDamage);
        this._totalDamageDealt += splashDamage;
        this._secondaryTargetsHit++;
      }
    }
  }
}
